import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@shopflo/ui/icon";
import { Input } from "@shopflo/ui/input";
import { User, PencilSimple, Phone, Check, X } from "@phosphor-icons/react";
import { useUserContext } from "lib/contexts/UserProvider";
import { updateCustomerName } from "lib/utils/profileEmails";
import { IconButton } from "@shopflo/ui/icon-button";
import { bannerErrorToastV2, bannerSuccessToastV2 } from "lib/utils/toasters";
import { useLocale } from "lib/hooks/useLocale";

const ProfileDetails = () => {
  const { t } = useLocale();
  const {
    state: { user },
    actions: { setUserData },
  } = useUserContext();

  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameFieldRef = useRef<HTMLDivElement>(null);

  const name = isEditingName ? draftName : (user?.name || "");

  const handleEditName = () => {
    if (isEditingName) return;
    setDraftName(user?.name || "");
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setDraftName(user?.name || "");
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    const trimmedName = name.trim();

    if (!trimmedName || trimmedName === (user?.name || "")) {
      setIsEditingName(false);
      return;
    }

    try {
      const [firstName = "", ...lastNameParts] = trimmedName.split(/\s+/);
      const lastName = lastNameParts.join(" ");

      const response = await updateCustomerName({ firstName, lastName }) as { first_name?: string; last_name?: string };
      const updatedName = [response?.first_name, response?.last_name].filter(Boolean).join(" ") || trimmedName;
      setUserData({ ...user, name: updatedName });
      setIsEditingName(false);
      bannerSuccessToastV2(t("profile_toast_profile_updated_success"));
    } catch {
      setIsEditingName(false);
      bannerErrorToastV2(t("something_went_wrong"));
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === "Escape") {
      setIsEditingName(false);
    }
  };

  useEffect(() => {
    if (!isEditingName) return;
    nameInputRef.current?.focus();
  }, [isEditingName]);

  useEffect(() => {
    if (!isEditingName) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!nameFieldRef.current?.contains(event.target as Node)) {
        handleCancelEditName();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditingName]);

  return (
    <div className="flex flex-col gap-6">
      <div className="mt-4" ref={nameFieldRef}>
        <Input
          ref={nameInputRef}
          variant="filled"
          dimension="lg"
          label={t("full_name")}
          value={name}
          readOnly={!isEditingName}
          onClick={!isEditingName ? handleEditName : undefined}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={isEditingName ? handleNameKeyDown : undefined}
          startAdornment={<Icon IconComponent={User} size="sm" className="text-zinc-400" />}
          endAdornment={
            isEditingName ? (
              <div className="flex gap-1">
                <IconButton
                  IconComponent={X}
                  size="sm"
                  className="text-zinc-400 cursor-pointer"
                  variant="outline"
                  onClick={handleCancelEditName}
                  aria-label={t("cancel")}
                />

                <IconButton
                  IconComponent={Check}
                  size="sm"
                  className="text-green-600 cursor-pointer"
                  variant="primary"
                  onClick={handleSaveName}
                  aria-label={t("save")}
                />
              </div>
            ) : (
              <Icon
                IconComponent={PencilSimple}
                size="sm"
                className="text-zinc-400 cursor-pointer"
                onClick={handleEditName}
              />
            )
          }
        />
      </div>

      <div>
        <Input
          variant="filled"
          dimension="lg"
          label={t("phone_number")}
          value={user?.phone || ""}
          readOnly
          disabled
          startAdornment={<Icon IconComponent={Phone} size="sm" className="text-zinc-400" />}
        />
      </div>
    </div>
  );
};

export default ProfileDetails;
